import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getBlogPostBySlug } from '../lib/contentful';
import MainLayout from '../layouts/MainLayout';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import DOMPurify from 'dompurify';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blogPost', slug],
    queryFn: () => getBlogPostBySlug(slug || ''),
  });

  if (error) {
    return <div>Error loading blog post</div>;
  }

  if (isLoading || !post) {
    return <div>Loading...</div>;
  }

  return (
    <MainLayout
      title={post.seoFields?.fields?.title || post.title}
      description={post.seoFields?.fields?.description || post.shortDescription}
      keywords={post.seoFields?.fields?.keywords?.join(', ')}
      ogType="article"
      ogImage={post.seoFields?.fields?.ogImage?.fields?.file?.url || post.featuredImage?.fields?.file?.url}
    >
      <div className="container max-w-7xl mx-auto px-4 md:px-6 py-12 pt-24">
        <article className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
            <div className="text-muted-foreground">
              By {post.author?.fields?.name} • {new Date(post.publishedDate).toLocaleDateString()}
            </div>
          </div>

          {post.featuredImage && (
            <div className="mb-8">
              <img
                src={post.featuredImage.fields.file.url}
                alt={post.featuredImage.fields.title}
                className="w-full h-auto rounded-lg"
              />
            </div>
          )}

          <div className="prose dark:prose-invert max-w-none">
            {/* Render embed HTML content */}
            {post.embedHtml && (
              <div 
                className="my-8"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(
                    // Parse the JSON string if needed
                    typeof post.embedHtml === 'string' && post.embedHtml.startsWith('"') 
                      ? JSON.parse(post.embedHtml)
                      : post.embedHtml,
                    {
                      ADD_TAGS: ['iframe'],
                      ADD_ATTR: ['src', 'loading', 'width', 'height', 'frameborder', 'scrolling', 'title', 'class']
                    }
                  )
                }} 
              />
            )}

            {/* Render markdown content */}
            {post.markdownContent && (
              <div className="markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    h1: ({ children }) => <h1 className="text-4xl font-bold mt-8 mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-3xl font-bold mt-8 mb-4">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-2xl font-bold mt-6 mb-4">{children}</h3>,
                    h4: ({ children }) => <h4 className="text-xl font-bold mt-6 mb-4">{children}</h4>,
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline font-medium"
                      >
                        {children}
                      </a>
                    ),
                    ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>,
                    code: ({ node, inline, className, children, ...props }: any) => {
                      return inline ? (
                        <code className="bg-muted px-1.5 py-0.5 rounded-md font-mono text-sm" {...props}>
                          {children}
                        </code>
                      ) : (
                        <pre className="bg-muted p-4 rounded-lg overflow-x-auto">
                          <code className="font-mono text-sm" {...props}>
                            {children}
                          </code>
                        </pre>
                      );
                    },
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-6">
                        <table className="w-full text-left border-collapse table-auto">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
                    th: ({ children }) => (
                      <th className="px-4 py-3 text-left font-semibold border-b-2 border-border">{children}</th>
                    ),
                    td: ({ children }) => (
                      <td className="px-4 py-3 border-b border-border align-top">{children}</td>
                    ),
                    tr: ({ children }) => <tr className="hover:bg-muted/50">{children}</tr>
                  }}
                >
                  {post.markdownContent}
                </ReactMarkdown>
              </div>
            )}

            {/* Render rich text content if no markdown */}
            {!post.markdownContent && post.content && (
              <div className="rich-text-content">
                {documentToReactComponents(post.content)}
              </div>
            )}
          </div>
        </article>
      </div>
    </MainLayout>
  );
};

export default BlogPost;
